import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
    FormControlLabel, Input, Typography, makeStyles, FormGroup, Checkbox,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';

const useStyles = makeStyles(theme => ({
    choiceResponse: {
        border: theme.youwell.borders.inputBox,
        backgroundColor: theme.youwell.colors.inputBackground,
        padding: theme.spacing(2),
    },
    horizontal: {
    },
    choiceGroup: {
        flexDirection: 'column',
        '&$horizontal': {
            flexDirection: 'row',
        },
    },
    formControlRoot: {
        '$readOnly &': {
            cursor: 'text',
        },
    },
    formControlLabel: {
        margin: theme.spacing(1, 0),
        flex: '1 1 auto',

        '$correct &': {
            color: theme.youwell.colors.green,
        },
        '$inCorrect &': {
            color: theme.youwell.colors.red,
        },
    },
    freetextLabel: {
        display: 'flex',
        alignItems: 'center',
    },
    freetextLabelText: {
        marginRight: theme.spacing(1),
        minWidth: 80,
    },
    choiceFreetextInput: {
        border: `1px solid ${theme.palette.grey[200]}`,
        padding: theme.spacing(0, 1),
    },
    correct: {
        color: theme.youwell.colors.green,
    },
    inCorrect: {
        color: theme.youwell.colors.red,
    },
    checkBox: {
        color: theme.palette.primary.main,

        '$correct &': {
            color: theme.youwell.colors.green,
        },
        '$inCorrect &': {
            color: theme.youwell.colors.red,
        },
        '$readOnly &': {
            cursor: 'default',
        },
    },
    readOnly: {
    },
}), { name: 'MultipleChoice' });

const MultipleChoice = ({
    choiceConfig, selectedChoices, handleResponseUpdate, showScores, question, disabled, onCompletedValid, readOnly,
}) => {
    const classes = useStyles();
    const isDisabled = disabled || showScores;
    const translate = useSelector(state => getTranslate(state.localize));

    const handleMultiSelectFreetextChange = freetextId => (event) => {
        const responses = [...selectedChoices || []];
        const ix = responses.findIndex(d => d.id === freetextId);
        const updatedValue = { id: freetextId, score: 0, value: event.target.value };
        if (ix > -1) {
            responses[ix] = updatedValue;
        } else {
            responses.push(updatedValue);
        }

        handleResponseUpdate(responses);
    };

    const handleMultiSelectChange = answer => (event, checked) => {
        const responses = (selectedChoices || []).filter(d => d.id !== answer.id);
        const values = checked ? [...responses, answer] : responses;
        handleResponseUpdate(values);
        if (onCompletedValid) onCompletedValid();
    };

    const checkedResponses = selectedChoices?.reduce((prev, curr) => (
        { ...prev, [curr.id]: curr.value }
    ), {}) || {};

    const onFreetextBlur = () => {
        if (checkedResponses[choiceConfig.freetextChoiceId]) {
            if (onCompletedValid) onCompletedValid();
        }
    };
    const getScoreClass = (item) => {
        if (!showScores) {
            return null;
        }

        if (item.score > 0) {
            return classes.correct;
        }
        if ((selectedChoices || []).some(d => d.id === item.id)) {
            return classes.inCorrect;
        }
        return null;
    };

    return (
        <div className={classes.choiceResponse}>
            <FormGroup
                className={classnames(classes.choiceGroup, { [classes.horizontal]: choiceConfig?.horizontal, [classes.readOnly]: readOnly })}
            >
                {question.answers?.map(item => (
                    <FormControlLabel
                        key={item.id}
                        disabled={isDisabled}
                        control={(
                            <Checkbox
                                disabled={isDisabled}
                                checked={!!checkedResponses[item.id]}
                                onChange={handleMultiSelectChange(item)}
                                value={item.id}
                                className={classes.checkBox}
                            />
                        )}
                        className={getScoreClass(item)}
                        classes={{ label: classes.formControlLabel, root: classes.formControlRoot }}
                        label={item.value}
                    />
                ))}

                {choiceConfig?.freetextChoiceId && (
                    <FormControlLabel
                        disabled={isDisabled}
                        control={(
                            <Checkbox
                                disabled={isDisabled}
                                checked={choiceConfig.freetextChoiceId in checkedResponses}
                                onChange={handleMultiSelectChange({ id: choiceConfig.freetextChoiceId })}
                                value={checkedResponses[choiceConfig.freetextChoiceId] || ''}
                                className={classes.checkBox}
                            />
                        )}
                        classes={{ label: classes.formControlLabel, root: classes.formControlRoot }}
                        label={(
                            <div className={classes.freetextLabel}>
                                <Typography className={classes.freetextLabelText}>
                                    {choiceConfig.freetextChoiceLabel || translate('taskContent.freetextChoiceLabel')}
                                </Typography>
                                <Input
                                    placeholder={translate('taskContent.freetextChoice')}
                                    fullWidth
                                    disabled={isDisabled}
                                    value={checkedResponses[choiceConfig.freetextChoiceId] || ''}
                                    onChange={handleMultiSelectFreetextChange(choiceConfig.freetextChoiceId)}
                                    onBlur={onFreetextBlur}
                                    className={classes.choiceFreetextInput}
                                    disableUnderline
                                />
                            </div>
                        )}
                    />
                )}
            </FormGroup>
        </div>
    );
};

MultipleChoice.propTypes = {
    handleResponseUpdate: PropTypes.func,
    question: PropTypes.object.isRequired,
    choiceConfig: PropTypes.object,
    selectedChoices: PropTypes.array,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    showScores: PropTypes.bool,
    onCompletedValid: PropTypes.func,
};

MultipleChoice.defaultProps = {
    choiceConfig: null,
    selectedChoices: null,
    disabled: false,
    readOnly: false,
    handleResponseUpdate: null,
    showScores: false,
    onCompletedValid: null,
};

export default MultipleChoice;
